import React, { useState, useEffect } from "react";
import {
  FiUser,
  FiLock,
  FiBell,
  FiSave,
  FiEye,
  FiEyeOff,
  FiTrash2,
} from "react-icons/fi";
import { useAuth } from "../../auth/AuthProvider";
import Api from "../../axiosConfig";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

const AccountSettings = () => {
  const { user, updateUser, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState({
    personal: false,
    password: false,
    notifications: false,
    delete: false,
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [personalInfo, setPersonalInfo] = useState({
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    phone: user?.phone || "",
  });

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [notifications, setNotifications] = useState({
    emailUpdates: user?.notificationPreferences?.emailUpdates || false,
    smsUpdates: user?.notificationPreferences?.smsUpdates || false,
    marketingEmails: user?.notificationPreferences?.marketingEmails || false,
  });

  useEffect(() => {
    if (user) {
      setPersonalInfo({
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        phone: user.phone || "",
      });
      setNotifications({
        emailUpdates: user.notificationPreferences?.emailUpdates || false,
        smsUpdates: user.notificationPreferences?.smsUpdates || false,
        marketingEmails: user.notificationPreferences?.marketingEmails || false,
      });
    }
  }, [user]);

  const handleDeleteAccount = async () => {
    try {
      setLoading((prev) => ({ ...prev, delete: true }));
      await Api.delete("/v1/users/me");
      toast.success("Account deleted successfully");
      logout();
      navigate("/login");
    } catch (error) {
      console.error("Error deleting account:", error);
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to delete account"
      );
    } finally {
      setLoading((prev) => ({ ...prev, delete: false }));
      setShowDeleteModal(false);
    }
  };

  const handlePersonalInfoSubmit = async (e) => {
    e.preventDefault();
    setLoading((prev) => ({ ...prev, personal: true }));

    try {
      const response = await Api.patch("/v1/auth/profile", personalInfo);
      
      // The backend returns the updated user object directly
      if (response.data) {
        updateUser(response.data);
        toast.success("Personal information updated successfully");
      } else {
        throw new Error("Failed to update profile");
      }
    } catch (error) {
      // console.error("Profile update error:", error);
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to update profile"
      );
    } finally {
      setLoading((prev) => ({ ...prev, personal: false }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validatePassword = (password) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    const errors = [];
    if (password.length < minLength)
      errors.push("be at least 8 characters long");
    if (!hasUpperCase) errors.push("contain at least one uppercase letter");
    if (!hasLowerCase) errors.push("contain at least one lowercase letter");
    if (!hasNumbers) errors.push("contain at least one number");
    if (!hasSpecialChar) errors.push("contain at least one special character");

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate passwords
    if (formData.newPassword !== formData.confirmPassword) {
      toast.error("New password and confirm password do not match");
      return;
    }

    const passwordErrors = validatePassword(formData.newPassword);
    if (passwordErrors.length > 0) {
      toast.error(
        <div>
          Password must:
          <ul className="list-disc pl-4 mt-2">
            {passwordErrors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      );
      return;
    }

    try {
      setLoading((prev) => ({ ...prev, password: true }));
      await Api.post("/v1/auth/change-password", {
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword,
      });

      toast.success("Password changed successfully");
      setFormData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (error) {
      console.error("Error changing password:", error);
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to change password"
      );
    } finally {
      setLoading((prev) => ({ ...prev, password: false }));
    }
  };

  const handleNotificationSubmit = async (e) => {
    e.preventDefault();
    setLoading((prev) => ({ ...prev, notifications: true }));

    try {
      const response = await Api.patch("/v1/auth/notification-preferences", notifications);
      const updatedNotifications = response.data;
      updateUser({ ...user, notificationPreferences: updatedNotifications });
      setNotifications(updatedNotifications);
      toast.success("Notification preferences updated successfully");
    } catch (error) {
      console.error("Notifications update error:", error);
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to update notification preferences"
      );
    } finally {
      setLoading((prev) => ({ ...prev, notifications: false }));
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-gray-700">Loading...</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <h1 className="text-3xl font-bold text-gray-900 mb-6">Account Settings</h1>

          <div className="space-y-6">
            {/* Personal Information */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center mb-4">
                <FiUser className="mr-2 text-primary-dark" />
                <h2 className="text-xl font-semibold">Personal Information</h2>
              </div>
              <form onSubmit={handlePersonalInfoSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name
                    </label>
                    <input
                      type="text"
                      value={personalInfo.firstName}
                      onChange={(e) =>
                        setPersonalInfo({
                          ...personalInfo,
                          firstName: e.target.value,
                        })
                      }
                      className="w-full p-2 border rounded-md"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name
                    </label>
                    <input
                      type="text"
                      value={personalInfo.lastName}
                      onChange={(e) =>
                        setPersonalInfo({
                          ...personalInfo,
                          lastName: e.target.value,
                        })
                      }
                      className="w-full p-2 border rounded-md"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      value={user?.email || ""}
                      className="w-full p-2 border rounded-md bg-gray-100"
                      disabled
                      title="Email cannot be changed here"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={personalInfo.phone}
                      onChange={(e) =>
                        setPersonalInfo({ ...personalInfo, phone: e.target.value })
                      }
                      className="w-full p-2 border rounded-md"
                      required
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={loading.personal}
                  className="mt-4 flex items-center px-4 py-2 bg-primary-dark text-white rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading.personal ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Saving...
                    </>
                  ) : (
                    <>
                      <FiSave className="mr-2" />
                      Save Changes
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Password Change */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center mb-4">
                <FiLock className="mr-2 text-primary-dark" />
                <h2 className="text-xl font-semibold">Change Password</h2>
              </div>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Current Password */}
                <div>
                  <label
                    htmlFor="currentPassword"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Current Password
                  </label>
                  <div className="relative">
                    <input
                      type={showCurrentPassword ? "text" : "password"}
                      id="currentPassword"
                      name="currentPassword"
                      value={formData.currentPassword}
                      onChange={handleChange}
                      required
                      className="block w-full px-4 py-2 border border-gray-300 border-solid rounded-md shadow-sm focus:ring-primary-dark focus:border-primary-dark"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showCurrentPassword ? (
                        <FiEyeOff className="h-5 w-5" />
                      ) : (
                        <FiEye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>

                {/* New Password */}
                <div>
                  <label
                    htmlFor="newPassword"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showNewPassword ? "text" : "password"}
                      id="newPassword"
                      name="newPassword"
                      value={formData.newPassword}
                      onChange={handleChange}
                      required
                      className="block w-full px-4 py-2 border border-gray-300 border-solid rounded-md shadow-sm focus:ring-primary-dark focus:border-primary-dark"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showNewPassword ? (
                        <FiEyeOff className="h-5 w-5" />
                      ) : (
                        <FiEye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Confirm New Password */}
                <div>
                  <label
                    htmlFor="confirmPassword"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Confirm New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      id="confirmPassword"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      required
                      className="block w-full px-4 py-2 border border-gray-300 border-solid rounded-md shadow-sm focus:ring-primary-dark focus:border-primary-dark"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showConfirmPassword ? (
                        <FiEyeOff className="h-5 w-5" />
                      ) : (
                        <FiEye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Password Requirements */}
                <div className="rounded-md bg-gray-50 p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <FiLock className="h-5 w-5 text-gray-400" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-gray-800">
                        Password Requirements
                      </h3>
                      <div className="mt-2 text-sm text-gray-600">
                        <ul className="list-disc space-y-1 pl-5">
                          <li>At least 8 characters long</li>
                          <li>Must contain at least one uppercase letter</li>
                          <li>Must contain at least one lowercase letter</li>
                          <li>Must contain at least one number</li>
                          <li>Must contain at least one special character</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={loading.password}
                    className="px-6 py-2 bg-primary-dark text-white rounded-md hover:bg-primary-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    {loading.password ? (
                      <>
                        <FiLock className="animate-spin h-5 w-5" />
                        <span>Changing Password...</span>
                      </>
                    ) : (
                      <>
                        <FiLock className="h-5 w-5" />
                        <span>Change Password</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>

            {/* Notification Preferences */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center mb-4">
                <FiBell className="mr-2 text-primary-dark" />
                <h2 className="text-xl font-semibold">Notification Preferences</h2>
              </div>
              <form onSubmit={handleNotificationSubmit}>
                <div className="space-y-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="emailUpdates"
                      checked={notifications.emailUpdates}
                      onChange={(e) =>
                        setNotifications({
                          ...notifications,
                          emailUpdates: e.target.checked,
                        })
                      }
                      className="h-4 w-4 text-primary-dark focus:ring-primary-dark border-gray-300 border-[1px] border-solid rounded"
                    />
                    <label
                      htmlFor="emailUpdates"
                      className="ml-2 block text-sm text-gray-900"
                    >
                      Receive email updates about your deliveries
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="smsUpdates"
                      checked={notifications.smsUpdates}
                      onChange={(e) =>
                        setNotifications({
                          ...notifications,
                          smsUpdates: e.target.checked,
                        })
                      }
                      className="h-4 w-4 text-primary-dark focus:ring-primary-dark border-gray-300 border-[1px] border-solid rounded"
                    />
                    <label
                      htmlFor="smsUpdates"
                      className="ml-2 block text-sm text-gray-900"
                    >
                      Receive SMS updates about your deliveries
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="marketingEmails"
                      checked={notifications.marketingEmails}
                      onChange={(e) =>
                        setNotifications({
                          ...notifications,
                          marketingEmails: e.target.checked,
                        })
                      }
                      className="h-4 w-4 text-primary-dark focus:ring-primary-dark border-gray-300 border-[1px] border-solid rounded"
                    />
                    <label
                      htmlFor="marketingEmails"
                      className="ml-2 block text-sm text-gray-900"
                    >
                      Receive marketing emails and promotions
                    </label>
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={loading.notifications}
                  className="mt-4 flex items-center px-4 py-2 bg-primary-dark text-white rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading.notifications ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Saving...
                    </>
                  ) : (
                    <>
                      <FiSave className="mr-2" />
                      Save Preferences
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Delete Account Section */}
            {user && !user.isDeleted && (
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center mb-4">
                  <FiTrash2 className="mr-2 text-red-600" />
                  <h2 className="text-xl font-semibold text-red-600">Delete Account</h2>
                </div>
                <p className="text-gray-600 mb-4">
                  Once you delete your account, there is no going back. Please be certain.
                </p>
                <button
                  onClick={() => setShowDeleteModal(true)}
                  className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors"
                  disabled={loading.delete}
                >
                  {loading.delete ? "Deleting..." : "Delete Account"}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete Account Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/40 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-red-600 mb-4">Delete Account</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete your account? This action cannot be undone.
              You will lose access to all your data and active deliveries.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                disabled={loading.delete}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                disabled={loading.delete}
              >
                {loading.delete ? "Deleting..." : "Yes, Delete Account"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountSettings;
